import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import EmptyScreensReportsComponent from 'webapp/components/empty-screens/reports';
import ReportsIndexGridComponent from 'webapp/components/reports/index/grid';
import ReportsIndexTableComponent from 'webapp/components/reports/index/table';
import type { ReportCreateOrigin } from 'webapp/components/reports/popup-create-report';
import ReportsPopupCreateReportComponent from 'webapp/components/reports/popup-create-report';
import TemplatesPublicRecommendedReportsComponent from 'webapp/components/templates/public/recommended-reports';

import { viewSettingsNamePrefix } from 'webapp/models-custom/filter-sort/report';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';

export default class ReportsIndexRouteTemplate extends Component {
	@service('account') declare accountService: Service.Account;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('popup') declare popupService: Service.Popup;

	@tracked viewId = 'grid';
	@tracked isRecentlyDeletedFilterActive = false;

	constructor(owner: Owner, args: object) {
		super(owner, args);
		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.settingsName, true) || {};
		if (savedValues.viewId) {
			this.viewId = savedValues.viewId;
		}
	}

	get settingsName(): string {
		return `${viewSettingsNamePrefix}-${this.accountService.id}`;
	}

	get isTableView(): boolean {
		return this.viewId === 'table';
	}

	get transferedReportAccountTemplateNotifications(): ResolutionCenterItem[] {
		return this.appNotificationsService.transferedReportAccountTemplateNotifications;
	}

	get transferedReportPublicTemplateNotifications(): ResolutionCenterItem[] {
		return this.appNotificationsService.transferedReportPublicTemplateNotifications;
	}

	get hasReports(): boolean {
		return (
			this.transferedReportAccountTemplateNotifications.length > 0 ||
			this.transferedReportPublicTemplateNotifications.length > 0 ||
			this.mmReportsService.normalReportModelsWithDeleted.length > 0
		);
	}

	get showReportsList(): boolean {
		return this.billingService.reportsBasicEnabled && this.hasReports;
	}

	get hideRecommendedTemplates() {
		return (
			this.mmReportsService.normalReportModels.length >= 3 ||
			this.isRecentlyDeletedFilterActive
		);
	}

	get opensCreateReportModal(): boolean {
		return (
			!this.accountService.isDemoAccount &&
			this.aiChatService.isAiChatAvailable &&
			!this.navigationService.isMobileApp
		);
	}

	@action
	createReport(origin: ReportCreateOrigin): void {
		if (this.opensCreateReportModal) {
			this.popupService
				.createModal(ReportsPopupCreateReportComponent, {
					popupClass: 'popup-type-create-report',
				})
				.open({ origin, onCreateBlank: this.startBlankReport });
			return;
		}

		this.startBlankReport();
	}

	@action
	startBlankReport(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
			return;
		}

		this.mmReportsService.setReportCreationOrigin('page');
		this.analyticsService.event(TrackingEvent.REPORT_NEW, { origin: 'page' });
		this.navigationService.transitionToRoute('reports.editor.report', 'null');
	}

	@action
	openView(viewId: string): void {
		this.viewId = viewId;
		this.viewSettingsManagerService.save(this.settingsName, { viewId: this.viewId }, true);
	}

	@action
	updateRecentlyDeletedFilter(isActive: boolean): void {
		this.isRecentlyDeletedFilterActive = isActive;
	}

	<template>
		<div class="container__recommended">
			{{#if this.showReportsList}}
				<div id="reports-list" class="bottom-padding view-{{this.viewId}}">
					{{#if this.isTableView}}
						<ReportsIndexTableComponent
							@transferedReportAccountTemplateNotifications={{this.transferedReportAccountTemplateNotifications}}
							@transferedReportPublicTemplateNotifications={{this.transferedReportPublicTemplateNotifications}}
							@hasReports={{this.hasReports}}
							@openView={{this.openView}}
							@onReportCreateChoice={{this.createReport}}
							@opensCreateReportModal={{this.opensCreateReportModal}}
							@updateIsRecentlyDeletedFilterActive={{this.updateRecentlyDeletedFilter}}
						/>
					{{else}}
						<ReportsIndexGridComponent
							@transferedReportAccountTemplateNotifications={{this.transferedReportAccountTemplateNotifications}}
							@transferedReportPublicTemplateNotifications={{this.transferedReportPublicTemplateNotifications}}
							@hasReports={{this.hasReports}}
							@openView={{this.openView}}
							@onReportCreateChoice={{this.createReport}}
							@opensCreateReportModal={{this.opensCreateReportModal}}
							@updateIsRecentlyDeletedFilterActive={{this.updateRecentlyDeletedFilter}}
						/>
					{{/if}}
				</div>
			{{else}}
				<EmptyScreensReportsComponent @onCreate={{fn this.createReport "empty_screen"}} />
			{{/if}}

			<TemplatesPublicRecommendedReportsComponent
				@isHidden={{this.hideRecommendedTemplates}}
			/>
		</div>
	</template>
}
